import React, { useState, useRef } from "react";
import { Box, Typography, Button, Grid, FormControl, FormLabel, TextField, Select, MenuItem, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
  },
}));

const labelStyle = { color: "#8F95B2", fontSize: "13px" };

const generateComplaintId = () => {
  const num = Math.floor(Math.random() * 900) + 100;
  return `CMP${num}`;
};

export default function ComplaintForm({ handleClose }) {
  const [form, setForm] = useState({
    complaintId: generateComplaintId(),
    complaintName: "",
    email: "",
    contactNo: "",
    subject: "",
    category: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const fileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(form).forEach((k) => { if (!form[k]) newErrors[k] = "Required"; });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const formData = new FormData();
      Object.keys(form).forEach((k) => formData.append(k, form[k]));
      if (file) formData.append("attachment", file);
      await postData("admin/complaint/create", formData);
      setToast({ open: true, severity: "success", message: "Complaint submitted successfully" });
      setTimeout(handleClose, 1200);
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to submit complaint" });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: "24px" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Complaint Management</Typography>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Complaint ID</FormLabel>
            <StyledTextField size="small" name="complaintId" value={form.complaintId} InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f5f5f5", "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" } } }} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Date</FormLabel>
            <StyledTextField size="small" name="date" type="date" value={form.date} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.date} helperText={errors.date} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Student Name</FormLabel>
            <StyledTextField size="small" name="complaintName" value={form.complaintName} onChange={handleChange} error={!!errors.complaintName} helperText={errors.complaintName} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Email Id</FormLabel>
            <StyledTextField size="small" name="email" type="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Contact No</FormLabel>
            <StyledTextField size="small" name="contactNo" value={form.contactNo} onChange={handleChange} error={!!errors.contactNo} helperText={errors.contactNo} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Subject</FormLabel>
            <StyledTextField size="small" name="subject" value={form.subject} onChange={handleChange} error={!!errors.subject} helperText={errors.subject} />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Select Category</FormLabel>
            <Select size="small" name="category" value={form.category} onChange={handleChange} error={!!errors.category} sx={{ borderRadius: "10px" }}>
              <MenuItem value="Discipline">Discipline</MenuItem>
              <MenuItem value="Academic Issue">Academic Issue</MenuItem>
              <MenuItem value="Classroom Misbehaviour">Classroom Misbehaviour</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Description</FormLabel>
            <StyledTextField size="small" name="description" multiline rows={3} value={form.description} onChange={handleChange} error={!!errors.description} helperText={errors.description} />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            onClick={() => fileRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
            onDragOver={(e) => e.preventDefault()}
            sx={{ border: "2px dashed #D5D7DA", borderRadius: "10px", p: "24px", textAlign: "center", cursor: "pointer", backgroundColor: "#fafafa" }}
          >
            <input type="file" ref={fileRef} style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0])} />
            <CloudUploadOutlinedIcon sx={{ fontSize: "40px", color: "#8F95B2" }} />
            <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 1 }}>
              {file ? file.name : "Click to upload or drag and drop"}
            </Typography>
            <Typography sx={{ color: "#bbb", fontSize: "11px" }}>SVG, PNG, JPG or GIF (max. 800×400px)</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={handleClose} sx={{ color: "#414651", fontSize: "14px", textTransform: "none", px: 4, borderRadius: "10px", border: "1px solid #D5D7DA" }}>Cancel</Button>
        <Button type="submit" sx={{ backgroundColor: "#336599", color: "#fff", fontSize: "14px", textTransform: "none", px: 4, borderRadius: "10px" }}>Submit</Button>
      </Box>
    </Box>
  );
}
